import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import en from 'src/constants/en'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { ContentAnalysisApiService } from 'src/services/contentAnalysisApi'

export type CommentSentiment = 'negative' | 'neutral' | 'positive'

interface UpdateCommentSentimentArgs {
  commentIds: string[]
  id: string
  sentiment: CommentSentiment
}

interface UpdateCommentSentimentResponse {
  data: {
    sentiment: CommentSentiment
    updated: number
  }
  success: boolean
}

const V2_SENTIMENT_KEYS = [
  QueryKey.GET_CONTENT_ANALYSIS_V2_OVERVIEW,
  QueryKey.GET_CONTENT_ANALYSIS_TOPICS,
  QueryKey.GET_CONTENT_ANALYSIS_TOPIC_DETAIL,
  QueryKey.GET_CONTENT_ANALYSIS_CATEGORIES,
  QueryKey.GET_CONTENT_ANALYSIS_CATEGORY_COMMENTS,
]

interface UseUpdateContentAnalysisCommentSentimentMutationParams {
  onSuccess?: () => void
}

export const useUpdateContentAnalysisCommentSentimentMutation = ({
  onSuccess,
}: UseUpdateContentAnalysisCommentSentimentMutationParams = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ commentIds, id, sentiment }: UpdateCommentSentimentArgs) =>
      ContentAnalysisApiService.updateCommentsSentiment<UpdateCommentSentimentResponse>(
        id,
        { commentIds, sentiment }
      ),
    onError: () => {
      toast.error(en.CHANGE_SENTIMENT_TYPE_ERROR_MESSAGE)
    },
    onSuccess: () => {
      // The re-label rewrites rollups in three collections, so everything that
      // reads them refetches: the comments chart, the individual posts table
      // (per-post breakdown) and the list/detail sentiment cards.
      queryClient.invalidateQueries({
        queryKey: [QueryKey.GET_CONTENT_ANALYSIS_COMMENTS],
      })
      queryClient.invalidateQueries({
        queryKey: [QueryKey.GET_CONTENT_ANALYSIS_INDIVIDUAL_POSTS],
      })
      queryClient.invalidateQueries({
        queryKey: [QueryKey.GET_CONTENT_ANALYSIS_LIST],
      })
      queryClient.invalidateQueries({
        queryKey: [QueryKey.GET_CONTENT_ANALYSIS],
      })
      // The redesign's reads join sentiment live from the comments, so the
      // overview, both topic tables (and an open topic popup) and Comments
      // Analysis all move with a re-label. The weekly insight is stored text
      // and does not.
      V2_SENTIMENT_KEYS.forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key] })
      )
      toast.success(en.CHANGE_SENTIMENT_TYPE_SUCCESS_MESSAGE)
      onSuccess?.()
    },
  })
}
